import React, { useState, useEffect, useMemo, useRef } from 'react';
// import axios from 'axios';
import api from '../services/api';
import { getStudentAwards, getMyAwards } from '../services/awardService';
import { STAR_THRESHOLDS } from '../constants/rewards';
import { STUDENT_REWARDS_SUMMARY, STUDENT_ASSIGNMENTS, ATTEMPTS, THROTTLE_CHECK, STUDENT_TEACHERS } from '../config/Config';
import { getBillingSummary, createCheckoutSubscription } from '../services/billingService';

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ErrorBoundary } from '../components/ErrorBoundary';
import SupportDialog from '../components/SupportDialog';
import StudentLayout from '../components/layout/StudentLayout';
import { useFeatures } from '../contexts/FeatureContext';
import { 
  Typography, 
  Box, 
  Paper, 
  Container, 
  Grid,
  LinearProgress,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  ExitToApp,
  Person,
  Assignment,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  VpnKey as VpnKeyIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { 
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  EmojiEvents as EmojiEventsIcon,
  AccessTime as AccessTimeIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Bolt as BoltIcon,
  WorkspacePremium as WorkspacePremiumIcon
} from '@mui/icons-material';

// Mock data for the dashboard
const studentData = {
  name: 'John Doe',
  class: '10A',
  progress: 75,
  completedLessons: 15,
  totalLessons: 20,
  points: 1240,
  rank: 8,
  recentActivities: [
    { id: 1, type: 'lesson', title: 'Completed Lesson 5: Algebra Basics', time: '2 hours ago', icon: <CheckCircleIcon color="success" /> },
    { id: 2, type: 'quiz', title: 'Scored 92% on Geometry Quiz', time: '1 day ago', icon: <EmojiEventsIcon color="primary" /> },
    { id: 3, type: 'lesson', title: 'Started Lesson 6: Advanced Equations', time: '2 days ago', icon: <PlayCircleOutlineIcon color="action" /> },
  ],
  upcomingTasks: [
    { id: 1, type: 'assignment', title: 'Complete Chapter 4 Exercises', due: 'Tomorrow', subject: 'Mathematics' },
    { id: 2, type: 'quiz', title: 'Weekly Math Quiz', due: 'In 2 days', subject: 'Mathematics' },
    { id: 3, type: 'lesson', title: 'Watch Video: Trigonometry Basics', due: 'In 3 days', subject: 'Mathematics' },
  ]
};

const formatDisplayDate = (value) => {
  if (!value) return 'No due date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getScoreStars = (score) => {
  const normalized = Number(score) || 0;
  return STAR_THRESHOLDS.reduce((stars, [threshold, value]) => (
    normalized >= threshold ? value : stars
  ), 0);
};

const getNextStarTarget = (score) => {
  const normalized = Number(score) || 0;
  const nextThreshold = STAR_THRESHOLDS.find(([threshold]) => normalized < threshold);
  if (!nextThreshold) {
    return null;
  }
  return {
    threshold: nextThreshold[0],
    stars: nextThreshold[1],
    gap: Math.max(0, Math.round((nextThreshold[0] - normalized) * 100) / 100),
  };
};

// Main Dashboard component wrapped with ErrorBoundary
export default function Dashboard() {
  return (
    <ErrorBoundary>
      <DashboardContent />
    </ErrorBoundary>
  );
}

// Actual dashboard content that uses auth context
const DashboardContent = () => {
  const [openSupport, setOpenSupport] = React.useState(false);
  const [isDemoMode, setIsDemoMode] = React.useState(false);
  const [isThrottled, setIsThrottled] = React.useState(false);
  const { user, loading, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [messageData, setMessageData] = useState(null);
  const [subscriptionBanner, setSubscriptionBanner] = useState({ show: false, planCode: null, planName: null, bannerMessage: null });
  const [retryPaymentLoading, setRetryPaymentLoading] = useState(false);
  
  // Helper to proxy image urls
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('/api/exams/media-proxy/')) return url;
    if (url.includes('objectstorage') && url.includes('oraclecloud.com')) {
      try {
        const u = new URL(url);
        const parts = u.pathname.split('/');
        const idx = parts.indexOf('o');
        if (idx !== -1 && parts.length > idx + 1) {
          const objectName = parts.slice(idx + 1).join('/');
          return `/api/exams/media-proxy/0/image/?object_name=${encodeURIComponent(objectName)}`;
        }
      } catch (e) {}
    }
    return url.startsWith('http') ? url : (url.startsWith('/') ? url : `/${url}`);
  };
  // this is for feature check
  const { hasFeature } = useFeatures();
  const [awards, setAwards] = useState([]);
  const [studentData, setStudentData] = useState({
    name: user?.first_name || user?.name || 'Student',
    class: user?.class_name || 'N/A',
    progress: 0,
    completedTests: 0,
    totalTests: 0,
    totalPoints: 0,
    totalStars: 0,
    rank: 0,
    recentActivities: [],
    upcomingTasks: [],
    focusSubject: null,
    latestCompletedTest: null,
    pendingCount: 0,
    completionRate: 0,
    testsAwaitingWorksheet: 0,
  });

  // Check if demo mode is throttled
  const checkThrottleStatus = async () => {
    try {
      const response = await api.get(THROTTLE_CHECK, { 
        params: { key: 'demo_mode' } 
      });
      setIsThrottled(response.data.throttled);
    } catch (error) {
      console.error('[Demo] Throttle check failed:', error);
      // If check fails, assume not throttled to allow normal operation
      setIsThrottled(false);
    }
  };

  // Demo mode functionality
  const handleDemoModeToggle = () => {
    if (isThrottled) {
      return; // Don't allow toggle if throttled
    }
    setIsDemoMode(!isDemoMode);
  };

  // Check throttle status when demo mode is toggled ON
  useEffect(() => {
    // Only check throttle if demo mode is enabled
    if (!isDemoMode) {
      return;
    }
    
    const initializeDemo = async () => {
      try {
        // Check throttle status
        await checkThrottleStatus();
        // Note: We don't need to fetch demo user ID - backend handles it automatically
        // when demo_mode=true is passed in query params
      } catch (error) {
        console.error('Failed to check throttle status:', error);
        // Don't show error to user - demo mode will still work
      }
    };
    initializeDemo();
  }, [isDemoMode]); // Only run when demo mode changes
  
  const open = Boolean(anchorEl);
  // Collapse duplicate awards and keep a count
  const groupedAwards = useMemo(() => {
    const map = new Map();
    awards.forEach(a => {
      const id =
        a.award?.id ??               // normal case
        a.award_details?.id ??       // fallback
        a.id;                        // last-chance
      if (!id) return;
      if (map.has(id)) map.get(id).count += 1;
      else map.set(id, { award: a, count: 1 });
    });
    return [...map.values()];
  }, [awards]);


  // Fetch dashboard data when component mounts or user changes
  // Fetch awards once we know the user ID
  const awardsFetchRef = useRef(false);
  const awardsFetchTimeoutRef = useRef(null);
  
    useEffect(() => {
      if (!user) return;
      
      // Debounce: clear any pending fetch
      if (awardsFetchTimeoutRef.current) {
        clearTimeout(awardsFetchTimeoutRef.current);
      }
      
      // Reset fetch flag when user or demo mode changes
      awardsFetchRef.current = false;
      
      // Debounce the fetch to prevent rapid successive calls
      awardsFetchTimeoutRef.current = setTimeout(async () => {
        // Prevent duplicate fetches
        if (awardsFetchRef.current) {
          return;
        }
        awardsFetchRef.current = true;
      
      console.log('[Demo] Awards useEffect triggered...');

      const fetchAwardsSmart = async () => {
        // If logged in as a student, just fetch their own awards once and stop
        const isStudent = user.isStudent || user.is_student;
        if (isStudent) {
          // Students should only use the my_awards endpoint
          try {
              console.log('[Demo] fetchAwardsSmart called - Demo mode:', isDemoMode);
            console.log('[Demo] User is student:', isStudent);
            
            // For demo mode, use demo_mode parameter
            const params = isDemoMode ? { demo_mode: 'true' } : {};
            const my = await getMyAwards(params);
            if (Array.isArray(my) && my.length) {
              setAwards(my);
              return;
            }
          } catch(e) {
            console.warn('[Dashboard] getMyAwards error', e?.response?.status);
            // Students should not fallback to teacher endpoint - just set empty awards
            setAwards([]);
          }
          return;
        }

        // Teacher / admin – attempt sequential fallbacks to locate awards wherever stored
        const idsToTry = [user.id, 6, 2, 1, 3, 4, 5];
        for (const id of idsToTry) {
          try {
            const { data } = await getStudentAwards(id);
            if (Array.isArray(data) && data.length) {
              setAwards(data);
              return;
            }
          } catch (e) {
            if (e?.response?.status === 403) {
              continue; // forbidden for this id, try next
            }
            console.error('[Dashboard] award fetch error for id', id, e);
          }
        }
        setAwards([]);
      };

        await fetchAwardsSmart();
        awardsFetchRef.current = false;
      }, 300); // 300ms debounce
      
      return () => {
        if (awardsFetchTimeoutRef.current) {
          clearTimeout(awardsFetchTimeoutRef.current);
        }
      };
    }, [user, isDemoMode]);

  useEffect(() => {
    console.log('User object in Dashboard:', user);
    if (user) {
      console.log('User roles:', {
        isAdmin: user.isAdmin,
        isTeacher: user.isTeacher,
        isStudent: user.isStudent,
        is_admin: user.is_admin,
        is_teacher: user.is_teacher,
        is_student: user.is_student,
        rawData: user
      });
    }
  }, [user]); 

  // ✅ NEW LOGIC: Handle messages from location.state (e.g., after invitation-based registration)
  useEffect(() => {
    if (location.state?.message && !showMessage) {
      setMessageData({
        message: location.state.message,
        fromInvite: location.state.fromInvite || false,
        email: location.state.email,
        isInviteBased: location.state.isInviteBased || false,
        inviteTeacher: location.state.inviteTeacher,
        inviteCount: location.state.inviteCount
      });
      setShowMessage(true);
      navigate(location.pathname, { replace: true });
    }
    if (location.state?.banner) {
      setSubscriptionBanner((prev) => ({ ...prev, show: true, bannerMessage: location.state.banner }));
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname, showMessage]);

  const dashboardFetchRef = useRef(false);
  const dashboardFetchTimeoutRef = useRef(null);

  useEffect(() => {
      if (!user) return;
      
      // Debounce: clear any pending fetch
      if (dashboardFetchTimeoutRef.current) {
        clearTimeout(dashboardFetchTimeoutRef.current);
      }
      
      // Reset fetch flag when user or demo mode changes
      dashboardFetchRef.current = false;
      
      // Debounce the fetch to prevent rapid successive calls
      dashboardFetchTimeoutRef.current = setTimeout(async () => {
        // Prevent duplicate fetches
        if (dashboardFetchRef.current) {
          return;
        }
        dashboardFetchRef.current = true;
        
    try {
      setIsLoading(true);
      
      console.log('[Demo] useEffect triggered - fetching dashboard data...');
      
      // Prepare params for demo mode
      const params = isDemoMode ? { demo_mode: 'true' } : {};
          console.log('[Demo] fetchDashboardData called - Demo mode:', isDemoMode);
      
      // Fetch reward summary
      console.log('[Demo] Fetching rewards for', isDemoMode ? 'demo user' : 'current user');
      const rewardRes = await api.get(STUDENT_REWARDS_SUMMARY, { params });
      const { total_points = 0, total_stars = 0 } = rewardRes.data || {};
      // Fetch assignments and attempts
          // When in demo mode, make calls sequentially with small delays to avoid hitting throttle limit
          // When not in demo mode, make calls in parallel for better performance
          let assignmentsRes, attemptsRes;
          if (isDemoMode) {
            // Small delay between calls to spread out throttle records
            assignmentsRes = await api.get(STUDENT_ASSIGNMENTS, { params });
            await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
            attemptsRes = await api.get(ATTEMPTS, { params });
          } else {
            [assignmentsRes, attemptsRes] = await Promise.all([
        api.get(STUDENT_ASSIGNMENTS, { params }),
        api.get(ATTEMPTS, { params })
      ]);
          }
      let assignmentsData = assignmentsRes.data;
      const assignments = Array.isArray(assignmentsData) ? assignmentsData : assignmentsData?.results || [];
      
      let attemptsData = attemptsRes.data;
      const attempts = Array.isArray(attemptsData) ? attemptsData : attemptsData?.results || [];
      const submittedAttempts = attempts.filter(a => a.submitted && assignments.some(asg=>asg.test===a.test));
      const completedTests = submittedAttempts.length;
      // has_upload lives on the assignment object, not the attempt — mirror Tests.js hasWorksheetUploaded logic
      const testsAwaitingWorksheet = submittedAttempts.filter(a => {
        const asg = assignments.find(x => x.test === a.test);
        const hasUpload = asg?.has_upload === true || a.has_upload === true;
        const isMarked = asg?.is_marked === true || asg?.is_marked === 'Yes' || a.is_marked === true;
        const isGraded = asg?.grading_completed === true || asg?.grading_completed === 'Yes' || a.grading_completed === true;
        return !hasUpload && !isMarked && !isGraded;
      }).length;
      const averagePercent = computeAverageScore(submittedAttempts, assignments.length);
      const totalTests = assignments.length;
      // Determine pending assignments (not yet in submitted attempts)
      const pendingAssignments = assignments.filter(asg => !attempts.some(at => at.test === asg.test && at.submitted));
      // Sort newest first and take 5
      const upcomingTasks = pendingAssignments
        .sort((a,b)=> new Date(b.assigned_at) - new Date(a.assigned_at))
        .slice(0,5)
        .map(asg => ({
          id: asg.id,
          title: asg.test_title,
          subject: asg.subject_name,
          due: asg.target_date,
          overdue: asg.target_date && new Date(asg.target_date) < new Date()
        }));

      const assignmentByTestId = new Map(assignments.map(asg => [Number(asg.test), asg]));
      const recentActivities = submittedAttempts
        .map((attempt) => {
          const assignment = assignmentByTestId.get(Number(attempt.test));
          const completedAt = attempt.completed_date || attempt.attempt_end_time || attempt.attempt_submitted_at || attempt.submitted_at || attempt.completed_at;
          return {
            id: attempt.id || attempt.test,
            title: attempt.test_title || assignment?.test_title || 'Completed test',
            subject: assignment?.subject_name || attempt.subject_name || 'Practice',
            score: Number(attempt.score) > 1 ? Number(attempt.score) : Math.round((Number(attempt.score) || 0) * 100),
            completedAt,
          };
        })
        .sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0))
        .slice(0, 3);

      const subjectPerformance = submittedAttempts.reduce((acc, attempt) => {
        const assignment = assignmentByTestId.get(Number(attempt.test));
        const subject = assignment?.subject_name || attempt.subject_name;
        if (!subject) return acc;
        const score = Number(attempt.score);
        const normalizedScore = Number.isNaN(score) ? 0 : (score > 1 ? score : score * 100);
        if (!acc[subject]) {
          acc[subject] = { totalScore: 0, count: 0 };
        }
        acc[subject].totalScore += normalizedScore;
        acc[subject].count += 1;
        return acc;
      }, {});

      const focusSubjectEntry = Object.entries(subjectPerformance)
        .map(([subject, value]) => ({
          subject,
          average: value.count ? Math.round((value.totalScore / value.count) * 100) / 100 : 0,
          count: value.count,
        }))
        .sort((a, b) => a.average - b.average)[0] || null;
        
        // Update state with actual data
        setStudentData(prev => ({
          ...prev,
          name: user.first_name || user.name || 'Student',
          class: user.class_name || 'N/A',
          progress: averagePercent, // Use actual average score from database
          completedTests,
          totalTests,
          averagePercent,
          totalPoints: total_points,
          totalStars: total_stars, // Use actual stars from database
          rank: prev.rank,
          recentActivities,
          upcomingTasks,
          focusSubject: focusSubjectEntry,
          latestCompletedTest: recentActivities[0] || null,
          pendingCount: pendingAssignments.length,
          completionRate: totalTests > 0 ? Math.round((completedTests / totalTests) * 100) : 0,
          testsAwaitingWorksheet,
        }));
        
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
          dashboardFetchRef.current = false;
      }
      }, 300); // 300ms debounce
      
      return () => {
        if (dashboardFetchTimeoutRef.current) {
          clearTimeout(dashboardFetchTimeoutRef.current);
        }
      };
  
  }, [user, isDemoMode] );

  const computeAverageScore = (completedTests, totalTests) => {
    console.log('computeAverageScore called with:', { completedTests, totalTests });
  
    if (!completedTests || !Array.isArray(completedTests) || completedTests.length === 0 || totalTests === 0) {
      console.log('Returning 0 due to invalid input');
      return 0;
    }
  
    const totalScore = completedTests.reduce((acc, test) => {
      // Handle different score formats
      let score = test?.score;
      if (score === undefined || score === null) {
        console.warn('Undefined or null score found:', test);
        return acc;
      }
      
      // Convert to number
      score = Number(score);
      if (isNaN(score)) {
        console.warn('Invalid score found:', test);
        return acc;
      }
  
      // If score is greater than 1, assume it's a percentage (e.g., 85 for 85%)
      // If score is less than or equal to 1, assume it's a decimal (e.g., 0.85 for 85%)
      const normalizedScore = score > 1 ? score : score * 100;
      
      return acc + normalizedScore;
    }, 0);
    
    // Calculate average percentage
    const average = totalScore / completedTests.length;
    const result = Math.round(average * 100) / 100; // Round to 2 decimal places
    
    return result;
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated()) {
      navigate('/login', { 
        state: { 
          from: '/dashboard',
          message: 'Please log in to access the dashboard' 
        } 
      });
    }
  }, [loading, isAuthenticated, navigate]);

  // Phase 5: Banner logic
  // - For paid plans: show payment banner when subscription inactive/suspended and unfinished.
  // - For Student Starter (free): show "accept invitation" banner only if there is at least one pending invite.
  useEffect(() => {
    if (!user) return;
    getBillingSummary()
      .then((data) => {
        const sub = data?.subscription;
        if (!sub) return;
        const status = sub.subscription_status || 'active';
        const completed = sub.payment_completed !== false;
        if ((status === 'inactive' || status === 'suspended') && !completed && sub.plan_code) {
          if (sub.plan_code === 'student_starter') {
            // For free Student Starter, base banner purely on pending invites.
            api.get(STUDENT_TEACHERS)
              .then((resp) => {
                const teachers = resp.data?.teachers || [];
                const hasPending = Array.isArray(teachers) && teachers.some(
                  (t) => t.is_accepted === 'awaiting'
                );
                if (hasPending) {
                  setSubscriptionBanner({
                    show: true,
                    planCode: null,
                    planName: sub.plan_name || sub.plan_code,
                    bannerMessage: "Accept your teacher's or parent's invitation to connect."
                  });
                } else {
                  setSubscriptionBanner({ show: false, planCode: null, planName: null, bannerMessage: null });
                }
              })
              .catch(() => {
                // On error, don't spam the user with a banner.
                setSubscriptionBanner({ show: false, planCode: null, planName: null, bannerMessage: null });
              });
            return;
          }
          setSubscriptionBanner((prev) => ({
            ...prev,
            show: true,
            planCode: sub.plan_code,
            planName: sub.plan_name || sub.plan_code,
          }));
        }
      })
      .catch(() => {});
  }, [user]);

  const handleRetryPayment = async () => {
    if (!subscriptionBanner.planCode || retryPaymentLoading) return;
    setRetryPaymentLoading(true);
    try {
      const data = await createCheckoutSubscription(subscriptionBanner.planCode, '/dashboard', '/payment-failed');
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error('Retry payment failed:', err);
    } finally {
      setRetryPaymentLoading(false);
    }
  };
  
  // Show loading state while checking auth or loading data
  if (loading || !user) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Close any open menus/dialogs
      handleClose();
      
      // Show loading state
      setIsLoading(true);
      
      // Call the logout function from AuthContext
      await logout();
      
      // AuthContext will handle the navigation
      
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, still try to clear everything and redirect
      try {
        // Clear all auth data
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        
        // Use React Router navigation
        navigate('/login', { replace: true });
      } catch (e) {
        console.error('Error during fallback logout:', e);
        // Last resort: clear storage and redirect
        localStorage.clear();
        sessionStorage.clear();
        navigate('/login', { replace: true });
      }
    }
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleTests = () => {
    handleClose();
    navigate('/tests');
  };

  const handleViewAssignedTests = () => {
    navigate('/tests', { state: { tab: 'assigned' } });
  };

  const handleUploadWorksheet = () => {
    navigate('/tests', { state: { tab: 'completed' } });
  };

  const handleRevisionNotes = () => {
    navigate('/revision-notes');
    handleClose();
  };

  const handleResults = () => {
    handleClose();
    navigate('/results');
  };

  const handleChangePassword = () => {
    handleClose();
    navigate('/settings/change-password');
  };

  // Determine if user is a student
  const isStudent = user?.isStudent || user?.is_student || (user?.role_name && user?.role_name.toString().toLowerCase() === 'student');

  // Extract values from studentData for student layout
  const averageScore = studentData.progress || 0;
  const completedTests = studentData.completedTests || 0;
  const testsAwaitingWorksheet = studentData.testsAwaitingWorksheet || 0;
  const totalTests = studentData.totalTests || 0;
  const totalPoints = studentData.totalPoints || 0;
  const totalStars = studentData.totalStars || 0;
  const completionRate = studentData.completionRate || 0;
  const nextStarTarget = getNextStarTarget(averageScore);
  const earnedProgressStars = getScoreStars(averageScore);
  const pendingCount = studentData.pendingCount || 0;
  const greetingName = user?.first_name || user?.name || 'Student';
  const motivationalLine = nextStarTarget
    ? `You're ${nextStarTarget.gap}% away from ${nextStarTarget.stars} ${nextStarTarget.stars === 1 ? 'star' : 'stars'} on your average score.`
    : completedTests > 0
      ? 'Outstanding work. You have reached the top milestone on your current average.'
      : 'Your first completed test will unlock your first milestone.';
  const nextActionTitle = pendingCount > 0 ? 'Start your next test' : 'Keep your momentum going';
  const nextActionDescription = pendingCount > 0
    ? `${pendingCount} ${pendingCount === 1 ? 'test is' : 'tests are'} ready for you. Focus on the next one and keep your progress moving.`
    : 'No assigned tests are waiting right now. Jump back in when a new practice task appears.';
  const recentActivities = studentData.recentActivities || [];
  const latestCompletedTest = studentData.latestCompletedTest;
  const focusSubject = studentData.focusSubject;
  const statCards = [
    {
      title: 'Average score',
      value: `${averageScore}%`,
      caption: completedTests > 0 ? 'Across submitted tests' : 'Complete a test to see your score',
      color: 'primary.main',
      icon: <TrendingUpIcon color="primary" />,
    },
    {
      title: 'Tests completed',
      value: `${completedTests}/${totalTests}`,
      caption: totalTests > 0 ? `${completionRate}% of assigned tests done` : 'No assigned tests yet',
      color: 'text.primary',
      icon: <AssignmentIcon color="primary" />,
    },
    {
      title: 'Points earned',
      value: totalPoints,
      caption: 'Rewards for completing work on time',
      color: 'success.main',
      icon: <BoltIcon color="success" />,
    },
    {
      title: 'Stars earned',
      value: earnedProgressStars,
      caption: 'Based on your current average score',
      color: 'warning.main',
      icon: <StarIcon color="warning" />,
    },
  ];

  // If student, use StudentLayout
  if (isStudent) {
    return (
      <StudentLayout onHelpClick={() => setOpenSupport(true)}>
        {subscriptionBanner.show && (
          <Alert
            severity="warning"
            action={
              subscriptionBanner.planCode ? (
                <Button color="inherit" size="small" onClick={handleRetryPayment} disabled={retryPaymentLoading}>
                  {retryPaymentLoading ? <CircularProgress size={20} /> : 'Retry Payment'}
                </Button>
              ) : (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/profile', { state: { activeMenu: 'teachers' } })}
                >
                  View My Teachers
                </Button>
              )
            }
            sx={{ mx: 2, mt: 2 }}
          >
            {subscriptionBanner.bannerMessage || `Complete your payment to activate your subscription${subscriptionBanner.planName ? ` (${subscriptionBanner.planName})` : ''}.`}
          </Alert>
        )}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: { xs: 2.5, md: 3.5 },
                  border: '1px solid',
                  borderColor: 'primary.light',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, ${theme.palette.primary.light}28 40%, ${theme.palette.warning.light}22 100%)`,
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Chip
                      label={pendingCount > 0 ? 'Next step ready' : 'Progress snapshot'}
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 2, fontWeight: 700 }}
                    />
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5, lineHeight: 1.1 }}>
                      Welcome back, {greetingName}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 1, fontWeight: 700 }}>
                      {motivationalLine}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
                      {pendingCount > 0
                        ? `You have ${pendingCount} pending ${pendingCount === 1 ? 'test' : 'tests'}, ${completedTests} completed, and an average score of ${averageScore}%.`
                        : `You have completed ${completedTests} ${completedTests === 1 ? 'test' : 'tests'} with an average score of ${averageScore}%.`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isDemoMode}
                            onChange={handleDemoModeToggle}
                            color="primary"
                            disabled={isThrottled}
                          />
                        }
                        label="Demo Mode"
                      />
                      {isThrottled && (
                        <Typography variant="caption" color="error.main">
                          Rate limit reached. Try again later.
                        </Typography>
                      )}
                      {isDemoMode && (
                        <Alert severity="info" sx={{ py: 0, alignItems: 'center' }}>
                          Demo mode is active. Showing data for demo@student.com
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        p: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
                        Your next action
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                        {nextActionTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {nextActionDescription}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        {pendingCount > 0 && (
                          <Button
                            variant="contained"
                            onClick={handleViewAssignedTests}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              px: 3,
                              py: 1.2,
                              fontWeight: 700,
                              boxShadow: '0 14px 32px rgba(56, 92, 255, 0.22)',
                            }}
                          >
                            Start your next test
                          </Button>
                        )}
                        {testsAwaitingWorksheet > 0 && (
                          <Button
                            variant="outlined"
                            onClick={handleUploadWorksheet}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              px: 3,
                              py: 1.2,
                              fontWeight: 700,
                            }}
                          >
                            Upload Worksheet
                          </Button>
                        )}
                      </Box>
                      {latestCompletedTest && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Last completed
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {latestCompletedTest.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {latestCompletedTest.subject} | {latestCompletedTest.score || 0}%
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                {statCards.map((card) => (
                  <Grid item xs={12} sm={6} lg={3} key={card.title}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 30px rgba(23, 34, 79, 0.08)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {card.title}
                          </Typography>
                          {card.icon}
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: card.color, mb: 0.5 }}>
                          {card.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {card.caption}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  {isDemoMode && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                      <Chip label="DEMO DATA" color="warning" variant="outlined" size="small" />
                      {isThrottled && <Chip label="RATE LIMITED" color="error" variant="outlined" size="small" />}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Milestone progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Turn each completed test into stronger scores, more stars, and your next reward.
                      </Typography>
                    </Box>
                    <Chip
                      icon={<WorkspacePremiumIcon />}
                      label={nextStarTarget ? `${nextStarTarget.gap}% to next star` : 'Top milestone reached'}
                      color={nextStarTarget ? 'primary' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ position: 'relative', pt: 1, pb: 5, mb: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(averageScore, 100)}
                      sx={{
                        height: 16,
                        borderRadius: 8,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 8,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        },
                      }}
                    />
                    {/* Current position dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '1px',
                        left: `clamp(8px, ${Math.min(averageScore, 100)}%, calc(100% - 8px))`,
                        transform: 'translateX(-50%)',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        border: `3px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />
                    <Box sx={{ position: 'absolute', top: 24, left: 0, right: 0, pointerEvents: 'none' }}>
                      {STAR_THRESHOLDS.map(([threshold, stars]) => (
                        <Box
                          key={`${threshold}-${stars}`}
                          sx={{
                            position: 'absolute',
                            left: `${threshold}%`,
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <StarIcon sx={{ fontSize: 18, color: averageScore >= threshold ? 'warning.main' : 'action.disabled' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25, fontWeight: averageScore >= threshold ? 700 : 400 }}>
                            {threshold}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary">
                          Current milestone
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                          {[...Array(5)].map((_, index) => (
                            <StarIcon key={index} color={index < earnedProgressStars ? 'warning' : 'disabled'} sx={{ fontSize: 22 }} />
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Based on your {averageScore}% average score.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary">
                          Completion progress
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                          {completionRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {completedTests} of {totalTests} assigned tests completed.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>


            <Grid item xs={12}>
              <Grid container spacing={3} sx={{ height: '100%' }}>
                <Grid item xs={12}>
                  <Card elevation={0} sx={{ borderRadius: 3, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ height: '100%', p: { xs: 2.5, md: 3 } }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Awards / Badges
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Every completed step builds toward recognitions you can see and keep.
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        {awards.length === 0 ? (
                          <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                              Your first badge is waiting
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Complete more tests and keep your scores moving up to unlock your first award.
                            </Typography>
                          </Paper>
                        ) : (
                          <Grid container spacing={1.5}>
                            {groupedAwards.map(({ award, count }) => {
                              const titleRaw = award.award_details?.name || award.award?.name || award.award_details?.category || award.award?.category || award.award_details?.description || award.award?.description || '';
                              const title = (titleRaw && typeof titleRaw === 'string') ? titleRaw.split(':')[0].trim() : titleRaw;
                              return (
                                <Grid item xs={4} sm={3} md={2} key={award.id}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 2,
                                      p: 1,
                                      textAlign: 'center',
                                      height: '100%',
                                      transition: 'transform 0.2s ease',
                                      '&:hover': { transform: 'translateY(-2px)' },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={getImageUrl(award.award_details?.image_url || award.award?.image_url)}
                                      alt={title}
                                      sx={{ width: 36, height: 36, objectFit: 'contain', mb: 0.5 }}
                                    />
                                    <Typography variant="caption" display="block" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.2 }}>
                                      {title}
                                    </Typography>
                                    {count > 1 && (
                                      <Chip label={`x${count}`} size="small" color="primary" variant="outlined" sx={{ mt: 0.5, height: 16, fontSize: '0.6rem' }} />
                                    )}
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>


          </Grid>
          <SupportDialog open={openSupport} onClose={()=>setOpenSupport(false)} presetEmail={user?.email || ''} />
          
          {/* ✅ NEW: Message Snackbar for invitation-based registration success (Student Layout) */}
          <Snackbar
            open={showMessage}
            autoHideDuration={6000}
            onClose={() => setShowMessage(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowMessage(false)} 
              severity="success" 
              variant="filled"
              sx={{ minWidth: 400 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {messageData?.fromInvite ? 'Welcome to Your Teacher\'s Group!' : 'Success!'}
              </Typography>
              <Typography variant="body2">
                {messageData?.message}
              </Typography>
              {messageData?.isInviteBased && messageData?.inviteTeacher && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    You are now connected with {messageData.inviteTeacher.name || messageData.inviteTeacher.email}
                    {messageData.inviteCount > 1 && ` and ${messageData.inviteCount - 1} other teacher(s)`}
                  </Typography>
                </Box>
              )}
            </Alert>
          </Snackbar>
        </Container>
      </StudentLayout>
    );
  }

  // For non-students, use the original layout
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {subscriptionBanner.show && (
        <Alert
          severity="warning"
          action={
            subscriptionBanner.planCode ? (
              <Button color="inherit" size="small" onClick={handleRetryPayment} disabled={retryPaymentLoading}>
                {retryPaymentLoading ? <CircularProgress size={20} /> : 'Retry Payment'}
              </Button>
            ) : null
          }
          sx={{ mx: 2, mt: 2 }}
        >
          {subscriptionBanner.bannerMessage || `Complete your payment to activate your subscription${subscriptionBanner.planName ? ` (${subscriptionBanner.planName})` : ''}.`}
        </Alert>
      )}
      {/* App Bar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              ThinkStyne
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mr: 1 }}>
                  {user?.first_name || user?.name || 'User'}
                </Typography>
                {user?.role_name === 'ADMIN' && (
                  <Chip 
                    label="Admin" 
                    size="small" 
                    color="error" 
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {user?.role_name === 'TEACHER' && (
                  <Chip 
                    label="Teacher" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {user?.role_name === 'STUDENT' && (
                  <Chip 
                    label="Student" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            )}
            <Tooltip title="Account settings">
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={open}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleTests}>
              <ListItemIcon>
                <Assignment fontSize="small" />
              </ListItemIcon>
              <ListItemText>Tests</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleRevisionNotes}>
              <ListItemIcon>
                <NotesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Revision Notes</ListItemText>
            </MenuItem>
            {hasFeature('performance_analytics') && (
              <MenuItem onClick={handleResults}>
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Results</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleChangePassword}>
              <ListItemIcon>
                <VpnKeyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Change Password</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Welcome back, {studentData.name}! Your Average Score is {studentData.averagePercent}%</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
               

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={studentData.averagePercent} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                 
                </Box>

                {/* milestone ticks & legend */}
                <Box sx={{ position:'relative', mt: 1, pb: 3 }}>
                  {/* milestone triangles */}
                  {STAR_THRESHOLDS.map(([pct]) => (
                    <Box
                      key={pct}
                      sx={{
                        position: 'absolute',
                        left: `${pct}%`,
                        transform: 'translateX(-50%)',
                        top: -8,
                        width: 0,
                        height: 0,
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderBottom: '8px solid',
                        borderBottomColor: 'primary.main',
                      }}
                    />
                  ))}

                  {/* milestone captions */}
                  {STAR_THRESHOLDS.map(([pct, star]) => (
                    <Typography
                      key={star}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${pct}%`,
                        transform: 'translateX(-50%)',
                        top: 0,
                      }}
                    >
                      {star}★
                    </Typography>
                  ))}

                  {/* 0% and 100% labels */}
                  <Typography variant="caption" sx={{ position: 'absolute', left: 0 }}>0%</Typography>
                  <Typography variant="caption" sx={{ position: 'absolute', right: 0, textAlign: 'right' }}>100%</Typography>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={4} md={4}>
                  <Typography variant="h5" fontWeight="bold">
                    {studentData.completedTests}/{studentData.totalTests}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests Completed
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={4} md={4}>
                  <Typography variant="h5" fontWeight="bold">
                    {studentData.totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Test Completed on time = 5 points)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4} md={4}>
                  <Box display="flex" alignItems="center">
                    {[1, 2, 3, 4, 5].map(n => (
                      <StarIcon key={n} sx={{ color: n <= studentData.totalStars ? 'gold' : 'grey.400' }} />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Stars
                  </Typography>
                </Grid>
              </Grid>


              </CardContent>
            </Card>

            {/* Awards / Badges */}
            <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Awards / Badges
            </Typography>

            {groupedAwards.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No awards yet.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {groupedAwards.map(({ award: a, count }) => {
                  const isCircular = (a.award_details?.name || a.award?.name || '').toLowerCase().includes('super'); // Customize logic
                  const titleRaw = a.award_details?.name || a.award?.name || a.award_details?.category || a.award?.category || a.award_details?.description || a.award?.description || '';
                  const title = (titleRaw && typeof titleRaw === 'string') ? titleRaw.split(':')[0].trim() : titleRaw;

                  return (
                    <Grid item key={a.id}>
                      <Box sx={{ position: 'relative', textAlign: 'center' }}>
                        {/* Outer Ring */}
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: isCircular ? '50%' : 2,
                            overflow: 'hidden',
                            border: '3px solid',
                            borderColor: 'grey.300',
                            bgcolor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                          }}
                        >
                          <Box
                            component="img"
                            src={getImageUrl(a.award_details?.image_url || a.award?.image_url)}
                            alt={title}
                            sx={{
                              width: '60%',
                              height: '60%',
                              objectFit: 'contain',
                              borderRadius: isCircular ? '50%' : 1,
                            }}
                          />
                        </Box>

                        {/* Count Bubble */}
                        {count > 1 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 22,
                              height: 22,
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              border: '2px solid white',
                            }}
                          >
                            {count}
                          </Box>
                        )}

                        {/* Award title below image */}
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ mt: 1, fontWeight: 500, color: 'text.primary' }}
                        >
                          {title}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
            
            </Card>
          </Grid>
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Upcoming Tasks */}
            <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
              
                <Button
                  variant="contained"
                  sx={{ mb: 2, mx: 'auto', display: 'block', textTransform: 'none', bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                  onClick={handleViewAssignedTests}
                >
                  Start your next test
                </Button>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Tests to Complete
                </Typography>
                <List>
                  {studentData.upcomingTasks.slice(0,3).map((task, index) => (
                    <React.Fragment key={task.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'action.hover' }}>
                            <AssignmentIcon color="primary" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="subtitle2" fontWeight="medium">{task.title}</Typography>}
                          secondary={
                            <>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {task.subject}
                              </Typography>
                              <Box display="flex" alignItems="center" mt={0.5}>
                                <AccessTimeIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="caption" color={task.overdue ? 'error.main' : 'text.secondary'}>
                                  {task.due}
                                </Typography>
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                      {index < Math.min(3, studentData.upcomingTasks.length) - 1 && <Divider variant="middle" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Duplicate Awards card removed
            <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Awards / Badges
                </Typography>
                {awards.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No awards yet.</Typography>
                ) : (
                  <Grid container spacing={1}>
                    {awards.map((a) => (
                      <Grid item xs={4} key={a.id} textAlign="center">
                        <Box
                          component="img"
                          src={getImageUrl(a.award_details?.image_url || a.award?.image_url)}
                          alt={a.award_details?.name || a.award?.name}
                          sx={{ width: 60, height: 60, objectFit: 'contain' }}
                        />
                        <Typography variant="caption" display="block">
                          {(a.award_details?.name || a.award?.name || '').split(':')[0]}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            */}

            {/* Quick Actions */}
            <Card elevation={3} sx={{ borderRadius: 2, bgcolor: 'primary.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Need Help?
                </Typography>
                <Typography variant="body2" paragraph>
                  Our support team is here to help you with any questions or issues you might have.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setOpenSupport(true)}
                  fullWidth
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    textTransform: 'none',
                    fontWeight: 'medium'
                  }}
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      {/* Footer */}
      <Box component="footer" sx={{ py: 3, px: 2, mt: 'auto', backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} ThinkStyne. All rights reserved.
          </Typography>
        </Container>
      </Box>
      <SupportDialog open={openSupport} onClose={()=>setOpenSupport(false)} presetEmail={user?.email || ''} />
      
      {/* ✅ NEW: Message Snackbar for invitation-based registration success */}
      <Snackbar
        open={showMessage}
        autoHideDuration={6000}
        onClose={() => setShowMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowMessage(false)} 
          severity="success" 
          variant="filled"
          sx={{ minWidth: 400 }}
        >
          <Typography variant="subtitle1" gutterBottom>
            {messageData?.fromInvite ? 'Welcome to Your Teacher\'s Group!' : 'Success!'}
          </Typography>
          <Typography variant="body2">
            {messageData?.message}
          </Typography>
          {messageData?.isInviteBased && messageData?.inviteTeacher && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                You are now connected with {messageData.inviteTeacher.name || messageData.inviteTeacher.email}
                {messageData.inviteCount > 1 && ` and ${messageData.inviteCount - 1} other teacher(s)`}
              </Typography>
            </Box>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};
